import { createSlice } from "@reduxjs/toolkit";

/**
 * Background Report Slice
 *
 * Persists MLD reports that were generated while the user had navigated away
 * from the originating project. Keyed by projectId so the stored data can be
 * looked up when the user reopens the project.
 *
 * This slice is intentionally excluded from useResetProjectData so that the
 * persisted report survives project switches.
 *
 * State shape:
 * ─────────────────────────────────────────────────────────────────
 * reports   Map of projectId → { rawData, effectiveTemp }
 *           rawData       Raw MLD API response from getMLDJobStatus.
 *           effectiveTemp Temperature used when the report was generated.
 * ─────────────────────────────────────────────────────────────────
 */
const initialState = {
  /**
   * Completed background report data keyed by project ID (coerced to string).
   * Shape: { [projectId]: { rawData: object, effectiveTemp: number } }
   */
  reports: {},
};

const BackgroundReportSlice = createSlice({
  name: "backgroundReport",
  initialState,
  reducers: {
    /**
     * Persist a completed background report for the given project.
     *
     * Usage:
     *   dispatch(storeBackgroundReport({ projectId: "123", rawData: apiResp, effectiveTemp: 25 }))
     */
    storeBackgroundReport: (state, action) => {
      const { projectId, rawData, effectiveTemp } = action.payload;
      state.reports[String(projectId)] = { rawData, effectiveTemp };
    },

    /**
     * Remove the persisted background report for the given project.
     * Called after the report has been restored and displayed to the user.
     *
     * Usage:
     *   dispatch(clearBackgroundReport("123"))
     */
    clearBackgroundReport: (state, action) => {
      delete state.reports[String(action.payload)];
    },
  },
});

export const { storeBackgroundReport, clearBackgroundReport } = BackgroundReportSlice.actions;
export default BackgroundReportSlice.reducer;
